/* eslint-disable react/no-unescaped-entities */
import React, { useContext, useState } from "react";
import {
  Upload,
  Check,
  Globe,
  Star,
  FileText,
  Shield,
  Trash2,
  Clock,
  Mail,
  CheckCircle2,
  X,
} from "lucide-react";
import TopBar from "../components/TopBar";
import Footerr from "../../src/components/Footerr/Footerr";
import { AuthContext } from "../Context/AuthContextProvider";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const PostaBid = () => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { addProduct, refreshProducts, loginUser } = useContext(AuthContext);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedDomain, setSubmittedDomain] = useState(null);

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    premium: false,
    speciality: "",
    price: "",
    renewal: "",
    ownerName: loginUser?.username || "",
    registered_date: "",
    rating: "",
    registrar: "",
  });

  const DomainCategory = [
    { label: "Jewelry Shop", value: "Jewelry Shop" },
    { label: "Jewelry Maker", value: "Jewelry Maker" },
    { label: "Gold Importer", value: "Gold Importer" },
    { label: "Gold Exporter", value: "Gold Exporter" },
    { label: "Gold Merchant", value: "Gold Merchant" },
    { label: "Diamond Merchant", value: "Diamond Merchant" },
    { label: "Gold Miners", value: "Gold Miners" },
    { label: "Gold Refiners", value: "Gold Refiners" },
    { label: "Gold Investors", value: "Gold Investors" },
    { label: "Gold Councils", value: "Gold Councils" },
    { label: "Gold Communities", value: "Gold Communities" },
    { label: "Gold Consortiums", value: "Gold Consortiums" },
    { label: "Others", value: "Others" },
  ];

  const DomainSpeciality = [
    { label: "3 letters", value: "3 letters" },
    { label: "4 letters", value: "4 letters" },
    { label: "5 letters", value: "5 letters" },
    { label: "Age Old Jewelry shop", value: "Age Old Jewelry shop" },
    { label: "Famous Family Name", value: "Famous Family Name" },
    { label: "Famous Celebrity Name", value: "Famous Celebrity Name" },
    { label: "Famous Company Name", value: "Famous Company Name" },
    { label: "Community Name", value: "Community Name" },
    { label: "Continent Name", value: "Continent Name" },
    { label: "Country Name", value: "Country Name" },
    { label: "State Name", value: "State Name" },
    { label: "District Name", value: "District Name" },
    { label: "City Name", value: "City Name" },
    { label: "Others", value: "Others" },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle price changes - auto-adjust premium status
    if (name === "price") {
      const priceValue = parseFloat(value) || 0;
      setFormData((prev) => ({
        ...prev,
        price: value,
        premium: priceValue >= 200000,
      }));
      return;
    }

    // Handle premium checkbox - validate price
    if (name === "premium" && checked) {
      const currentPrice = parseFloat(formData.price) || 0;
      if (currentPrice < 200000) {
        toast.error("Premium domains must have a minimum price of ₹2,00,000");
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + mediaFiles.length > 4) {
      return toast.error("Maximum 4 images allowed");
    }

    const oversizedFiles = files.filter((f) => f.size > 200 * 1024);
    if (oversizedFiles.length > 0) {
      return toast.error("Each image must be less than 200KB");
    }

    setMediaFiles((prev) => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const { name, category, description, price, ownerName, registered_date, renewal, premium } = formData;

    if (!name || !category || !description || !price || !ownerName) {
      toast.error("Please fill all required fields");
      return false;
    }

    const priceValue = parseFloat(price);
    if (priceValue < 1) {
      toast.error("Price must be a positive number");
      return false;
    }

    // Validate premium price
    if (premium && priceValue < 200000) {
      toast.error("Premium domains must have a minimum price of ₹2,00,000");
      return false;
    }

    if (
      formData.rating &&
      (parseFloat(formData.rating) < 1 || parseFloat(formData.rating) > 5)
    ) {
      toast.error("Rating must be between 1 and 5");
      return false;
    }

    // Validate 1-year gap between registration and renewal dates
    if (registered_date && renewal) {
      const regDate = new Date(registered_date);
      const renDate = new Date(renewal);
      const oneYearLater = new Date(regDate);
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);

      if (renDate < oneYearLater) {
        toast.error("Renewal date must be at least 1 year after registration date");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const submitData = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (
          formData[key] !== null &&
          formData[key] !== undefined &&
          formData[key] !== ""
        ) {
          submitData.append(key, formData[key]);
        }
      });

      // Append images with correct field name
      mediaFiles.forEach((file) => {
        submitData.append("images", file);
      });

      const resp = await addProduct(submitData);

      if (resp?.success) {
        setSubmittedDomain(formData.name);
        setShowSuccessModal(true);

        // Refresh products if available
        if (refreshProducts) {
          await refreshProducts();
        }
        navigate("/Dashboard/Mysubmissions");
      } else {
        toast.error(resp?.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      toast.error("Server error. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      category: "",
      description: "",
      premium: false,
      speciality: "",
      price: "",
      renewal: "",
      ownerName: loginUser?.username || "",
      registered_date: "",
      rating: "",
      registrar: "",
    });
    setMediaFiles([]);
  };

  const handleNewSubmission = () => {
    handleReset();
    setShowSuccessModal(false);
    setSubmittedDomain(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ✅ SUCCESS MODAL POPUP
  const SuccessModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-[fadeIn_0.3s_ease-in-out]">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#b99653] via-[#d4af37] to-[#b99653] p-8 text-center relative">
          <button
            onClick={() => setShowSuccessModal(false)}
            className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex justify-center mb-4">
            <div className="bg-white rounded-full p-4 shadow-lg">
              <CheckCircle2 className="w-16 h-16 text-[#d4af37]" />
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white mb-2">
            Submission Successful! 🎉
          </h2>
          <p className="text-white text-opacity-90">
            Your domain has been submitted for admin review
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Domain Info Box */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 mb-6 border-2 border-[#d4af37]">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-6 h-6 text-[#b99653]" />
              <h3 className="text-2xl font-bold text-[#101c4c]">
                {submittedDomain}
              </h3>
              {formData.premium && (
                <span className="bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  Premium
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Category:</span>
                <span className="font-semibold text-[#101c4c] ml-2">
                  {formData.category}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Price:</span>
                <span className="font-semibold text-[#101c4c] ml-2">
                  ₹{parseFloat(formData.price).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>

          {/* Timeline Steps */}
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-4">
              <div className="bg-[#d4af37] rounded-full p-2 flex-shrink-0">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-[#101c4c] mb-1">
                  Submission Received ✓
                </h4>
                <p className="text-sm text-gray-600">
                  Your domain submission has been successfully received and is
                  now in queue
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-blue-100 rounded-full p-2 flex-shrink-0 animate-pulse">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-bold text-[#101c4c] mb-1">
                  Admin Review in Progress
                </h4>
                <p className="text-sm text-gray-600">
                  Our team will review your submission within 24–48 hours
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 opacity-50">
              <div className="bg-gray-200 rounded-full p-2 flex-shrink-0">
                <Mail className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <h4 className="font-bold text-[#101c4c] mb-1">
                  Email Notification
                </h4>
                <p className="text-sm text-gray-600">
                  You will receive an email once your domain is approved
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={() => setShowSuccessModal(false)}
              className="flex-1 px-6 py-3 border-2 border-[#d4af37] text-[#b99653] rounded-xl font-semibold hover:bg-amber-50 transition-all duration-300"
            >
              Close
            </button>
            <button
              onClick={handleNewSubmission}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              <Upload className="w-5 h-5" />
              Submit Another Domain
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <TopBar />

      {/* Success Modal */}
      {showSuccessModal && <SuccessModal />}

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-24">
        <div className="max-w-6xl mx-auto px-4">
          {/* HEADER CARD */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-2 border-[#d4af37]">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#b99653] to-[#d4af37]">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-[#b99653] to-[#d4af37] bg-clip-text text-transparent">
                  Submit Your .gold Domain
                </h1>
                <p className="text-gray-600 mt-1">
                  List your domain for review by our admin team
                </p>
              </div>
            </div>
          </div>

          {/* INFO CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6 border-2 border-blue-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900">Admin Review</h3>
              </div>
              <p className="text-sm text-gray-600">
                All submissions are reviewed by our admin team before listing
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-2 border-green-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900">Fast Response</h3>
              </div>
              <p className="text-sm text-gray-600">
                You'll receive an email within 24–48 hours
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-2 border-amber-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Star className="w-5 h-5 text-[#d4af37]" />
                </div>
                <h3 className="font-bold text-gray-900">Premium Listing</h3>
              </div>
              <p className="text-sm text-gray-600">
                Featured placement for domains above ₹2,00,000
              </p>
            </div>
          </div>

          {/* MAIN FORM CARD */}
          <form onSubmit={handleSubmit}>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-blue-100">
              <div className="p-8">
                <div className="space-y-8">
                  {/* DOMAIN DETAILS */}
                  <div>
                    <h3 className="text-xl font-bold text-[#101c4c] mb-6 flex items-center gap-2 pb-3 border-b-2 border-[#d4af37]">
                      <Globe className="w-6 h-6 text-[#d4af37]" />
                      Domain Details
                    </h3>

                    <div className="space-y-6">
                 

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Domain Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-all"
                            placeholder="example.gold"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Category *
                          </label>
                          <select
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-all"
                          >
                            <option value="">Select Category</option>
                            {DomainCategory.map((cat) => (
                              <option key={cat.value} value={cat.value}>
                                {cat.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Speciality
                          </label>
                          <select
                            name="speciality"
                            value={formData.speciality}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-all"
                          >
                            <option value="">Select Speciality</option>
                            {DomainSpeciality.map((spec) => (
                              <option key={spec.value} value={spec.value}>
                                {spec.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Price (₹) *
                          </label>
                          <input
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleInputChange}
                            min="1"
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-all"
                            placeholder={formData.premium ? "Minimum ₹2,00,000" : "Enter price"}
                          />
                          {formData.premium && parseFloat(formData.price) < 200000 && (
                            <p className="text-xs text-red-600 mt-1">
                              Premium domains require minimum ₹2,00,000
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Description *
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows="4"
                          required
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37] transition-all"
                          placeholder="Describe your domain..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* ADDITIONAL INFO SECTION */}
                  <div>
                    <h3 className="text-xl font-bold text-[#101c4c] mb-6 flex items-center gap-2 pb-3 border-b-2 border-blue-200">
                      <FileText className="w-6 h-6 text-blue-600" />
                      Additional Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Owner Name *
                        </label>
                        <input
                          type="text"
                          name="ownerName"
                          value={formData.ownerName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50"
                          placeholder="Enter owner name"
                          readOnly
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Registrar
                        </label>
                        <input
                          type="text"
                          name="registrar"
                          value={formData.registrar}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder="e.g., GoDaddy, Namecheap"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Registered Date
                        </label>
                        <input
                          type="date"
                          name="registered_date"
                          value={formData.registered_date}
                          onChange={handleInputChange}
                          max={new Date().toISOString().split("T")[0]}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Renewal Date
                        </label>
                        <input
                          type="date"
                          name="renewal"
                          value={formData.renewal}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Rating (1-5)
                        </label>
                        <input
                          type="number"
                          name="rating"
                          value={formData.rating}
                          onChange={handleInputChange}
                          min="1"
                          max="5"
                          step="0.1"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          placeholder="Rate your domain"
                        />
                      </div>
                      
                    </div>
                         <label className="flex items-center gap-3 cursor-pointer p-4 bg-amber-50 rounded-xl border-2 border-[#d4af37] hover:bg-amber-100 transition-colors mt-6">
                        <input
                          type="checkbox"
                          name="premium"
                          checked={formData.premium}
                          onChange={handleInputChange}
                          className="w-5 h-5 text-[#d4af37] rounded focus:ring-[#d4af37]"
                        />
                        <Star className="w-5 h-5 text-[#d4af37] fill-current" />
                        <span className="text-sm font-semibold text-[#101c4c]">
                          Premium Domain (Minimum ₹2,00,000)
                        </span>
                      </label>
                  </div>

                  {/* MEDIA UPLOAD SECTION */}
                  <div>
                    <h3 className="text-xl font-bold text-[#101c4c] mb-6 flex items-center gap-2 pb-3 border-b-2 border-green-200">
                      <Upload className="w-6 h-6 text-green-600" />
                      Domain Images (Optional)
                    </h3>

                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#d4af37] hover:bg-amber-50 transition-all">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer flex flex-col items-center gap-3"
                      >
                        <div className="bg-amber-100 p-4 rounded-full">
                          <Upload className="w-8 h-8 text-[#d4af37]" />
                        </div>
                        <p className="text-base font-semibold text-gray-700">
                          Click to upload images
                        </p>
                        <p className="text-sm text-gray-500">
                          Max 4 images • Max 200KB each • JPG, PNG, GIF
                        </p>
                      </label>
                    </div>

                    {mediaFiles.length > 0 && (
                      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {mediaFiles.map((file, index) => (
                          <div
                            key={`${file.name}-${file.size}-${index}`}
                            className="relative group bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:border-[#d4af37] transition-all"
                          >
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="w-full h-40 object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <div className="p-2 bg-white">
                              <p className="text-xs text-gray-600 truncate font-medium">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-400">
                                {(file.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* FOOTER BUTTONS */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 flex items-center justify-end gap-4 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={handleReset}
                  className="white-btn "
                >
                  Reset Form
                </button>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="white-btn flex items-center gap-2 !border-[#fae441]"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Submit for Admin Review
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Footerr />
    </div>
  );
};

export default PostaBid;
